import { Box, Paper, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LanguageSelector } from "./LanguageSelector";
import { useStoreSettings } from "../contexts/StoreSettingsContext";

interface AuthShellProps {
  children: React.ReactNode;
  /** Show ghost language selector at top-right of the card. Default true. */
  showLangSelector?: boolean;
  /** Extra padding-top on the card (useful when the form is taller). Default "44px". */
  cardPaddingTop?: string | number;
}

/**
 * Shared shell for all pre-auth pages: full-screen background photo,
 * arc-top white card, logo medallion straddling the arc, ghost language selector.
 */
export function AuthShell({
  children,
  showLangSelector = true,
  cardPaddingTop = "44px",
}: AuthShellProps) {
  const { storeSettings } = useStoreSettings();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  return (
    <Box
      sx={{
        // minHeight only — no fixed height so the container can grow with content
        // and the fixed ::before background always covers the full screen regardless
        minHeight: "100svh",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        // desktop: center the card; mobile: pin to bottom as a bottom sheet
        justifyContent: isDesktop ? "center" : "flex-end",
        alignItems: isDesktop ? "center" : "stretch",
        "&::before": {
          content: '""',
          position: "fixed",
          inset: 0,
          background: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("${storeSettings?.backgroundUrl || "/images/background.png"}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          zIndex: 0,
        },
      }}
    >
      {/* Medallion + card wrapper */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: isDesktop ? "440px" : "100%",
          // room above card for the medallion to float
          pt: "32px",
        }}
      >
        {/* Logo medallion straddling the card's top edge */}
        <Box
          sx={{
            position: "absolute",
            top: "32px",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "#fff",
            boxShadow:
              "0 4px 20px rgba(0,0,0,0.22), 0 0 0 5px rgba(255,255,255,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3,
          }}
        >
          <img
            src={storeSettings?.logoUrl || "/icons/logo.svg"}
            alt=""
            style={{ height: "38px", width: "auto" }}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            borderRadius: isDesktop ? "24px" : "32px 32px 0 0",
            background: "#FFF",
            // Lock text dark: brand foregroundColor can be light (designed for dark bg),
            // but this card is always white so all uncolored text must be dark.
            "& .MuiTypography-root": { color: "#1A1A1A" },
            "& .MuiInputBase-input": { color: "#1A1A1A" },
            "& .MuiSelect-select": { color: "#1A1A1A" },
            "& .MuiFormControlLabel-label": { color: "#555" },
            p: 4,
            pt: cardPaddingTop,
            // Bottom padding: at least 24px; grows to clear the home-indicator on iPhone.
            // When the virtual keyboard opens dvh shrinks, so the card shrinks with it
            // and the content scrolls — the submit button stays reachable without
            // the keyboard hiding it.
            pb: "max(24px, env(safe-area-inset-bottom, 0px))",
            width: "100%",
            // dvh (dynamic viewport height) accounts for the virtual keyboard:
            // it shrinks when the keyboard is open, so the card never extends
            // behind the keyboard on iOS 15.4+ / Chrome 108+.
            // "32px" is the top pt of the wrapper (room for the medallion).
            maxHeight: isDesktop ? "80vh" : "calc(100dvh - 32px)",
            boxShadow: "0 -8px 40px rgba(0,0,0,0.3)",
            overflowY: "auto",
          }}
        >
          {showLangSelector && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <LanguageSelector variant="ghost" />
            </Box>
          )}

          {children}
        </Paper>
      </Box>
    </Box>
  );
}
